import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

export const BRAND = {
  charcoal: '#1A1A1B',
  teal: '#00A3A1',
  tealDark: '#007B79',
  tealSoft: '#6ED8D6',
  porcelain: '#F4F7F7',
};

export const pageMotion = {
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: 'easeOut' },
};

export const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

export const logoSrc = (settings) => settings?.logo_url || '/assets/logo.png';

export const BrandLogo = ({ settings, className = 'h-12', withName = false, compact = false }) => {
  const fallbackName = settings?.site_name_en || 'Raad Holding';

  return (
    <div className="flex items-center gap-3 min-w-0">
      <img
        src={logoSrc(settings)}
        alt={fallbackName}
        className={`${className} w-auto object-contain`}
        onError={(event) => {
          event.currentTarget.src = '/assets/logo.png';
        }}
      />
      {withName && !compact && (
        <span className="hidden sm:block text-sm font-black tracking-[0.22em] uppercase text-raad-charcoal">
          RAAD HOLDING
        </span>
      )}
    </div>
  );
};

export const BrandMark = ({ className = 'h-10 w-10' }) => (
  <img src="/assets/raad-mark-new.png" alt="Raad mark" className={`${className} object-contain`} />
);

export const SectionTag = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-3 text-xs font-black uppercase tracking-[0.32em] text-raad-teal ${className}`}>
    <span className="h-px w-8 bg-raad-teal" />
    {children}
  </span>
);

export const SectionHeader = ({ kicker, title, description, align = 'center', className = '' }) => {
  const alignment = align === 'start' ? 'text-start items-start' : 'text-center items-center mx-auto';

  return (
    <motion.div {...pageMotion} className={`flex flex-col ${alignment} ${className}`}>
      {kicker && <SectionTag>{kicker}</SectionTag>}
      {title && (
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-raad-charcoal text-balance">
          {title}
        </h1>
      )}
      {description && (
        <p className="mt-5 max-w-3xl text-base sm:text-lg leading-8 text-raad-graphite/75 text-balance">
          {description}
        </p>
      )}
    </motion.div>
  );
};

export const PageHero = ({ kicker, title, description, children }) => (
  <section className="brand-page-hero relative overflow-hidden pt-36 pb-16 lg:pt-40 lg:pb-20">
    <div className="absolute inset-0 bg-raad-porcelain" />
    <div className="brand-grid absolute inset-0 opacity-60" />
    <div className="absolute -top-28 end-[-10rem] h-80 w-80 rounded-full bg-raad-teal/12 blur-3xl" />
    <div className="absolute start-0 bottom-0 h-8 w-80 max-w-[70vw] bg-raad-teal/20 brand-slash" />
    <div className="container relative z-10 mx-auto px-6">
      <SectionHeader kicker={kicker} title={title} description={description} />
      {children}
    </div>
  </section>
);

export const LoadingScreen = () => (
  <div className="min-h-screen bg-raad-porcelain flex items-center justify-center pt-24">
    <div className="relative h-16 w-16">
      <span className="absolute inset-0 rounded-full border-2 border-raad-teal/20" />
      <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-raad-teal animate-spin" />
      <BrandMark className="absolute inset-3 h-10 w-10" />
    </div>
  </div>
);

export const EmptyState = ({ title, description, action }) => (
  <div className="mx-auto max-w-2xl rounded-[2rem] border border-raad-teal/15 bg-white p-10 text-center shadow-soft">
    <BrandMark className="mx-auto mb-5 h-12 w-12" />
    <h3 className="text-2xl font-black text-raad-charcoal">{title}</h3>
    {description && <p className="mt-3 text-raad-graphite/70">{description}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const BackLink = ({ to, label, isRTL }) => {
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  return (
    <Button asChild variant="ghost" className="rounded-full text-raad-charcoal hover:bg-raad-teal/10 hover:text-raad-teal">
      <Link to={to}>
        <BackIcon className="w-4 h-4 me-2" />
        {label}
      </Link>
    </Button>
  );
};

export const ArrowLinkIcon = ({ isRTL, className = '' }) => {
  const Icon = isRTL ? ArrowLeft : ArrowRight;
  return <Icon className={`h-4 w-4 ${className}`} />;
};

export const PhoneText = ({ children, className = '' }) => (
  <span dir="ltr" className={`phone-ltr inline-block ${className}`}>
    {children}
  </span>
);

export const CardShell = ({ children, className = '' }) => (
  <div className={`group relative overflow-hidden rounded-[1.75rem] border border-raad-teal/15 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-raad-teal/40 hover:shadow-teal ${className}`}>
    <div className="absolute start-0 top-0 h-full w-1 bg-raad-teal opacity-0 transition-opacity group-hover:opacity-100" />
    {children}
  </div>
);

export const TealButton = ({ to, children, isRTL, className = '', variant = 'solid', ...props }) => {
  const content = (
    <>
      {children}
      {isRTL !== undefined && <ArrowLinkIcon isRTL={isRTL} className="ms-2" />}
    </>
  );

  const classes = variant === 'outline'
    ? `rounded-full border border-raad-teal/40 bg-white text-raad-charcoal hover:bg-raad-teal hover:text-white ${className}`
    : `rounded-full bg-raad-charcoal text-white hover:bg-raad-teal ${className}`;

  if (to) {
    return (
      <Button asChild className={classes} {...props}>
        <Link to={to}>{content}</Link>
      </Button>
    );
  }

  return (
    <Button className={classes} {...props}>
      {content}
    </Button>
  );
};
